<template>
	<div
		ref="tipsSection"
		class="tip-selection"
		:class="{ 'tip-selection--dark': darkTheme }"
	>
		<p class="tip-selection__title">ADD A TIP</p>
		<ul class="tip-selection__list">
			<li
				v-for="tipItem in tipPercentCalculated"
				:key="tipItem.percentLabel"
				class="tip-selection__list__item"
				:class="{
					'tip-selection__list__item--selected':
						tipItem.calculatedValue === tip && !isInputOpenedManually,
					'tip-selection__list__item--disabled': tipsLoading
				}"
				@click="selectTipPerCent(tipItem)"
			>
				<span class="tip-selection__list__item__percent">
					{{ tipItem.percentLabel }}
				</span>
				<span class="tip-selection__list__item__value">
					<formatted-price
						:price="tipItem.calculatedValue"
						decimal-height="60"
					/>
				</span>
			</li>
			<li
				class="tip-selection__list__item tip-selection__list__item--other"
				:class="{
					'tip-selection__list__item--selected': isOtherShown,
					'tip-selection__list__item--disabled': tipsLoading || isOtherShown
				}"
				@click="openCustomTipsForm"
			>
				Custom
			</li>
		</ul>
		<div v-show="isOtherShown" class="tip-selection__input">
			<div class="input-field tip-input" v-click-outside="hideTipEditing">
				<input
					class="custom-input"
					type="text"
					inputmode="decimal"
					v-money="money"
					ref="integerTipsInput"
					:value="integerValue"
					maxlength="9"
					@focus="clearTipValue"
					@input="changeIntegerValue"
					@keydown="handleKeyDown"
					@blur="handleBlur"
				/>
				<input
					class="custom-input"
					type="text"
					inputmode="decimal"
					ref="fractionalTipsInput"
					:value="fractionalValue"
					placeholder="00"
					maxlength="2"
					@focus="clearTipValue"
					@input="changeFractionalValue"
					@keydown="handleKeyDown"
					@blur="handleBlur"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import { VMoney } from 'v-money'
// Utils
import {
	clickOutside,
	checkIsAvailableKeyForNumberField
} from '@/utils/helpers'
import { debounce } from 'lodash'
// Components
import FormattedPrice from '@/components/FormattedPrice.vue'
// Constants
import { KEYS, KEYS_CODE } from '@/constants'

export default {
	name: 'TipSelection',
	components: { FormattedPrice },

	directives: {
		'click-outside': clickOutside,
		money: VMoney
	},

	props: {
		tip: {
			type: [String, Number],
			default: 0
		},
		useTipsPercent: {
			type: Boolean,
			default: false
		},
		total: {
			type: [String, Number],
			default: 0
		},
		tipPercentValues: {
			type: Array,
			default: () => [15, 20, 25]
		},
		darkTheme: {
			type: Boolean,
			default: false
		},
		tipsLoading: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			integerValue: '',
			fractionalValue: '',
			integerTipsInput: null,
			fractionalTipsInput: null,
			isInputOpenedManually: false,
			isTipEditing: false,
			isPercentSelected: false,
			customTipsFocused: false,
			prevSavedTips: 0,
			money: {
				decimal: '',
				thousands: '',
				prefix: '$',
				suffix: '',
				precision: 0,
				masked: false
			}
		}
	},

	computed: {
		...mapState('ui', ['isDesktopDevice', 'isMacOrIOS']),

		tipPercentCalculated() {
			return this.tipPercentValues.map(el => {
				const percentLabel = `${el}%`
				const calculatedValue = (this.total * (el / 100)).toFixed(2)
				return {
					percentLabel,
					calculatedValue,
					percent: parseFloat(el),
					tip: parseFloat(calculatedValue)
				}
			})
		},
		isCustomSelected() {
			return !this.tipPercentCalculated.some(
				({ calculatedValue }) => calculatedValue === this.tip
			)
		},
		tipNumber() {
			return parseFloat(
				`${this.integerValue.replace('$', '')}.${this.fractionalValue}`
			)
		},
		tipPercent() {
			return Math.round((this.tipNumber / this.total) * 100) || 0
		},
		isOtherShown() {
			return this.isCustomSelected || this.isInputOpenedManually
		}
	},

	mounted() {
		this.initTips()
		this.integerTipsInput = this.$refs['integerTipsInput']
		this.fractionalTipsInput = this.$refs['fractionalTipsInput']
	},

	watch: {
		tip() {
			this.isInputOpenedManually = false
			this.initTips()
		}
	},

	methods: {
		selectTipPerCent(tipData) {
			this.isPercentSelected = true

			if (parseFloat(tipData.tip) === parseFloat(this.tip)) {
				this.isInputOpenedManually = false
			}

			this.$emit('apply-tip', { ...tipData, useTipsPercent: true })
		},

		disableTipEditing() {
			this.isTipEditing = false
		},
		hideTipEditing() {
			if (!this.isDesktopDevice) {
				this.addFocusClassToContainer()
			}

			this.isTipEditing = false
		},
		setDefaultTips(needScroll = false) {
			if (this.isCustomSelected) {
				this.clearTipValue()

				if (needScroll) {
					if (!this.isDesktopDevice) {
						this.scrollToTipsHeader()
					}
				}

				this.saveTips()
			}
		},
		handleKeyDown(e) {
			if (e.key === KEYS.enter) {
				document.activeElement?.blur && document.activeElement.blur()
			}

			if (
				(e.key === KEYS.dot ||
					// Android case
					(e.key === 'Unidentified' &&
						e.keyCode === KEYS_CODE.androidNumericComa) ||
					e.key === KEYS.coma) &&
				document.activeElement === this.$refs['integerTipsInput']
			) {
				if (this.isDesktopDevice) {
					this.$refs['fractionalTipsInput'].focus({ preventScroll: true })
					e.preventDefault()
				} else {
					this.$refs['fractionalTipsInput'].focus({ preventScroll: true })
				}

				return
			}

			if (
				e.keyCode === KEYS_CODE.backSpace &&
				e.target.selectionStart === 0 &&
				document.activeElement === this.$refs['fractionalTipsInput']
			) {
				this.integerTipsInput.focus({ preventScroll: true })
				e.preventDefault()
			}

			if (!checkIsAvailableKeyForNumberField(e)) {
				e.preventDefault()
			}
		},
		debouncedApplyTips: debounce(function() {
			if (this.tipNumber === this.tip && !this.useTipsPercent) return
			const focusedEl = document.activeElement

			if (
				focusedEl?.classList &&
				Array.from(focusedEl.classList).includes('custom-input')
			) {
				this.saveTips()
			}
		}, 2000),
		changeIntegerValue(e) {
			this.integerValue = e.target.value
			this.integerValue = this.integerValue.replaceAll('-', '')
			this.debouncedApplyTips()
		},
		changeFractionalValue(e) {
			const val = e.target.value
			if (val.length > 2) {
				e.preventDefault()
			}
			this.fractionalValue = e.target.value
			this.fractionalValue = this.fractionalValue
				.replaceAll('-', '')
				.replaceAll(',', '')
				.replaceAll('.', '')
			this.debouncedApplyTips()
		},
		removeTipsFocusOnIOS() {
			if (!this.isDesktopDevice && this.isMacOrIOS) {
				if (document.activeElement === this.$refs['integerTipsInput']) {
					this.$refs['integerTipsInput'].blur()
				} else if (
					document.activeElement === this.$refs['fractionalTipsInput']
				) {
					this.$refs['fractionalTipsInput'].blur()
				}
			}
		},
		clearTipValue() {
			if (!this.isTipEditing) {
				this.isTipEditing = true

				this.integerValue = ''
				this.fractionalValue = ''
				this.integerTipsInput.focus({ preventScroll: true })
				this.scrollToTipsHeader(true)
			}
		},
		initTips() {
			const isInputFocused =
				document.activeElement === this.$refs['integerTipsInput'] ||
				document.activeElement === this.$refs['fractionalTipsInput']

			if (this.tip && !isInputFocused) {
				const tipChunks = parseFloat(this.tip)
					.toFixed(2)
					.toString()
					.split('.')
				this.integerValue = tipChunks[0]
				this.fractionalValue = tipChunks[1]
			}
		},
		handleBlur(e) {
			const newFocus = e.relatedTarget

			if (
				newFocus === this.$refs['integerTipsInput'] ||
				newFocus === this.$refs['fractionalTipsInput']
			) {
				return
			}

			this.disableTipEditing()

			setTimeout(() => {
				// percent selected or apply button when focus is on tips input
				if (this.isPercentSelected) return

				this.saveTips()
			}, 200)
		},
		saveTips() {
			if (
				(this.tipNumber === this.tip && !this.useTipsPercent) ||
				(this.prevSavedTips === this.tipNumber && Number(this.prevSavedTips))
			)
				return

			const tip = this.tipNumber || 0

			this.$emit('apply-tip', {
				tip,
				percent: this.tipPercent,
				useTipsPercent: false
			})

			this.prevSavedTips = tip
		},

		openCustomTipsForm() {
			this.isPercentSelected = false
			this.isInputOpenedManually = true

			if (this.integerTipsInput) {
				this.$nextTick(() => {
					this.clearTipValue()
					this.saveTips()

					if (!this.isDesktopDevice) {
						this.scrollToTipsHeader()
					}
				})
			}
		},

		scrollToTipsHeader(needAddClass = false) {
			setTimeout(() => {
				if (needAddClass) {
					if (!this.isDesktopDevice) {
						this.addFocusClassToContainer()
					}
				}

				const tipsHeader = this.$refs['tipsSection']

				if (tipsHeader) {
					tipsHeader.scrollIntoView({
						behavior: 'auto',
						block: 'start',
						inline: 'nearest'
					})
				}

				this.$emit('tips-loading', false)
			}, 150)
		},

		addFocusClassToContainer() {
			const content = document.querySelector('.check-wrap')
			const focusedClass = 'tips-focused'

			const isAnyInputFocused =
				document.activeElement === this.integerTipsInput ||
				document.activeElement === this.fractionalTipsInput

			if (content) {
				content.classList.toggle(focusedClass, isAnyInputFocused)
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

.tip-selection {
	margin: 0 auto 16px auto;

	&__title {
		color: $white;
		font-size: 14px;
		margin-bottom: 8px;
		text-transform: uppercase;
		text-align: center;
	}

	&__list {
		margin: 0;
		padding: 0;
		list-style: none;
		display: flex;
		align-items: center;
		justify-content: stretch;
		flex-shrink: 0;
		flex-grow: 0;

		&__item {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			width: 25%;
			max-width: 136px;
			padding: 8px;
			margin-right: 16px;
			text-align: center;
			color: $blue-faceless-void;
			border: 1px solid $border-color;
			background-color: $deep-violet;
			border-radius: 12px;
			font-size: 18px;
			cursor: pointer;
			transition: color 0.3s, background-color 0.3s, border-color 0.3s,
				opacity 0.3s;

			&:first-child {
				margin-left: auto;
			}

			&:last-child {
				margin-right: auto;
			}

			@media (min-width: 1024px) {
				&:hover {
					background-color: rgba($light-blue, 0.25);
				}
			}

			&__percent {
				display: block;
				margin-bottom: 4px;
				font-size: 20px;
				font-weight: 300;
			}

			&__value {
				font-size: 16px;
				line-height: 22px;
			}

			&--selected {
				color: $white;
				background-color: rgba($light-blue, 0.25);
				border-color: $pink;
			}

			&--other {
				font-size: 20px;
				height: 70px;
			}

			&--disabled {
				pointer-events: none;
			}

			@media (max-width: 480px) {
				margin-right: 8px;

				&__percent,
				&--other {
					font-size: 18px;
				}

				&__value {
					font-size: 14px;
				}
			}
		}
	}

	&__input {
		margin-top: 16px;

		input {
			width: 100%;
			font-size: 32px !important;
			font-weight: 300;
			text-align: center;
			border: none;
			border-bottom: 1px solid $grey-dark;
		}
	}

	.tip-input {
		padding: 0;
		margin: 0 0 20px;
		overflow: hidden;
		position: relative;
		font-size: 30px;
		border-bottom: 1px solid $black;
		transition: border-color 0.3s;

		&:after {
			position: absolute;
			left: 50%;
			top: 8px;
			font-size: 32px !important;
			font-weight: 300;
			content: '.';
			color: $dark-blue;
			pointer-events: none;
		}

		input {
			float: left;
			text-align: left;
			width: 50%;
			min-width: 30px;
			padding: 6px 10px 6px 7px;
			font-size: 30px;
			color: $dark-blue;
			border: 0;

			&::-webkit-input-placeholder {
				color: $dark-blue;
				line-height: normal;
			}

			&:first-child {
				padding-right: 0;
				text-align: right;
			}

			&:focus {
				::-webkit-input-placeholder {
					color: transparent;
					line-height: normal;
				}
			}
		}
	}

	&--dark {
		.tip-input {
			border-color: $border-color;

			.custom-input {
				color: $white;

				&::-webkit-input-placeholder {
					color: $white;
				}
			}

			&:after {
				color: $white;
			}

			&:focus-within {
				border-bottom-color: $light-purple;
			}
		}

		.tip-selection__list__item,
		.tip-selection__title {
			color: $white;
		}
	}
}
</style>
